import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";

import { Icon } from "../components/Icon";
import { DASHBOARD_RAISE_KEY, RaiseTicketLink } from "../components/RaiseTicketLink";
import { features } from "../config/features";
import {
  api,
  type AutotaskTicketListResponse,
  type DashboardStats,
  type Me,
  type PendingApprovalRow,
} from "../services/api";
import { slaPill, statusBadgeClass } from "../utils/ticketStatus";

const PLACEHOLDER = "—"; // reserved for genuine no-data

const OFFLINE_NOTICE = "You're offline. Your dashboard will update when you reconnect.";
const FAILURE_NOTICE = "Some of your dashboard couldn't be loaded. We'll keep trying.";
const STATS_UNAVAILABLE = "Not available right now";
const RECENT_UNAVAILABLE = "Recent tickets aren't available right now.";

function browserOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

/** Mirrors the browser online and offline events for the page notice. */
function useBrowserOnline(): boolean {
  const [online, setOnline] = useState(browserOnline);
  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);
  return online;
}

/** Skip fetches while the browser is offline. A paused error is not retried,
 *  and SWR still revalidates on the `online` event, which fills the cards
 *  back in. An online failure keeps SWR's default retry. */
const dashboardSwr = {
  isPaused: () => !browserOnline(),
};

function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined) return PLACEHOLDER;
  return value.toLocaleString("en-AU");
}

function isAllHealthy(stats: DashboardStats | undefined): boolean {
  if (!stats) return false;
  const slaOk =
    stats.sla_total_30d_count === 0 ||
    (stats.sla_met_30d_percent !== null && stats.sla_met_30d_percent >= 95);
  const openOk = (stats.my_open_tickets ?? 0) === 0;
  return slaOk && openOk;
}

/** "Ryan Lochowicz" or "Lochowicz, Ryan" -> "Ryan"; empty when there is no usable name. */
function firstName(displayName: string | null | undefined): string {
  const name = (displayName ?? "").trim();
  const given = name.includes(",") ? name.split(",")[1] : name;
  return given.trim().split(/\s+/)[0] ?? "";
}

/** Time-of-day greeting in the viewer's local time, e.g. "Good morning, Ryan." */
function greeting(displayName: string | null | undefined): string {
  const hour = new Date().getHours();
  const part = hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
  const name = firstName(displayName);
  return name ? `Good ${part}, ${name}.` : `Good ${part}.`;
}

function fmtAge(hours: number | null | undefined): string {
  if (hours === null || hours === undefined) return "—";
  if (hours < 24) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

function priorityClass(label: string | null | undefined): string {
  if (!label) return "priority-medium";
  const l = label.toLowerCase();
  if (l.includes("critical")) return "priority-critical";
  if (l.includes("high")) return "priority-high";
  if (l.includes("medium")) return "priority-medium";
  if (l.includes("low")) return "priority-low";
  return "priority-medium";
}

/** "2h ago" / "yesterday" / "3 Jun" — concise relative time for the approval rail. */
function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

/** Initials for an approval-row avatar, e.g. "Priya Nair" -> "PN". */
function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]![0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

/** "new_user_request" -> "New user request" for a readable approval title. */
function humanizeSlug(slug: string | null | undefined): string {
  if (!slug) return "Approval request";
  const s = slug.replace(/[_-]+/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "Approval request";
}

export function Dashboard() {
  const online = useBrowserOnline();
  const { data: me, error: meError, mutate: mutateMe } = useSWR<Me>(
    "/api/me",
    () => api.me(),
    dashboardSwr,
  );
  const {
    data: stats,
    error: statsError,
    mutate: mutateStats,
  } = useSWR<DashboardStats>("/api/dashboard/stats", () => api.dashboard.stats(), {
    ...dashboardSwr,
    refreshInterval: 300000,
  });
  // Whether this user may read company-wide tickets, derived from identity
  // alone. Deliberately independent of `stats`: the Recent Tickets fetch below
  // needs the answer before the stats call resolves, and asking for a scope the
  // backend refuses (services/tickets.py `_assert_scope_allowed`) 403s, which
  // SWR reports as no data at all. That renders as an indefinite "Loading…",
  // so a plain end_user used to sit on a panel that never resolved.
  const canViewCompanyTickets =
    me !== undefined &&
    (me.role === "iquest_admin" ||
      me.role === "service_desk" ||
      me.role === "client_admin" ||
      me.permissions.includes("view_all_company_tickets"));

  const recentScope = me?.role === "iquest_admin" || me?.role === "service_desk"
    ? "all"
    : canViewCompanyTickets
      ? "company"
      : "my";
  const {
    data: recentTickets,
    error: recentError,
    mutate: mutateRecent,
  } = useSWR<AutotaskTicketListResponse>(
    me ? ["/api/tickets/recent", recentScope] : null,
    () => api.tickets.list({ scope: recentScope, page: 1, page_size: 6 }),
    dashboardSwr,
  );

  // Only reach for pending approvals once the engine reports ready — otherwise
  // the endpoint isn't meaningful and the rail card simply doesn't render.
  const approvalsReady = stats?.approval_engine_status === "ready";
  const {
    data: pendingApprovals,
    error: approvalsError,
    mutate: mutateApprovals,
  } = useSWR<PendingApprovalRow[]>(
    approvalsReady ? "/api/approvals/pending" : null,
    () => api.approvals.listPending(),
    dashboardSwr,
  );

  // A hook that has never succeeded. A later refresh error keeps the last
  // payload, so it must not flip a loaded card back to the empty figure.
  const statsUnavailable = stats === undefined && (!online || Boolean(statsError));
  const recentUnavailable =
    recentTickets === undefined &&
    (!online || Boolean(recentError) || (me === undefined && Boolean(meError)));
  const onlineFailure =
    online &&
    ((me === undefined && Boolean(meError)) ||
      (stats === undefined && Boolean(statsError)) ||
      (recentTickets === undefined && Boolean(recentError)) ||
      (approvalsReady && pendingApprovals === undefined && Boolean(approvalsError)));

  const headingRef = useRef<HTMLHeadingElement>(null);
  const retryRef = useRef<HTMLButtonElement>(null);
  const retryHadFocus = useRef(false);
  const retryingRef = useRef(false);
  const [retrying, setRetrying] = useState(false);
  const sawRetry = useRef(false);
  // The button unmounts once the retry succeeds. Keep keyboard focus on the page
  // only when it had focus and nothing else has taken it.
  useEffect(() => {
    if (onlineFailure || !retryHadFocus.current) return;
    retryHadFocus.current = false;
    if (document.activeElement === document.body) headingRef.current?.focus();
  }, [onlineFailure]);
  // A failed retry leaves the notice up. Drop the flag when focus has already
  // left Try again, so a later automatic recovery does not move it. Keep the
  // flag when Try again still has focus, so that recovery lands on the heading.
  useEffect(() => {
    if (
      sawRetry.current &&
      !retrying &&
      onlineFailure &&
      document.activeElement !== retryRef.current
    ) {
      retryHadFocus.current = false;
    }
    sawRetry.current = retrying;
  }, [retrying, onlineFailure]);

  // Try again only while online: isPaused drops a mutate started offline.
  // mutate() skips dedupe, so a ref blocks extra clicks until this attempt ends.
  function retryFailed() {
    if (!browserOnline() || retryingRef.current) return;
    retryingRef.current = true;
    setRetrying(true);
    retryHadFocus.current = document.activeElement === retryRef.current;
    const jobs: Promise<unknown>[] = [];
    if (meError) jobs.push(mutateMe());
    if (statsError) jobs.push(mutateStats());
    if (recentError) jobs.push(mutateRecent());
    if (approvalsError) jobs.push(mutateApprovals());
    void Promise.all(jobs).finally(() => {
      retryingRef.current = false;
      setRetrying(false);
    });
  }

  // Same predicate as the fetch scope above, held back until `stats` has
  // loaded so the card does not flash "No access" while the page is still
  // resolving.
  const noCompanyAccess =
    stats !== undefined && me !== undefined && !canViewCompanyTickets;

  const hasSla =
    !!stats && stats.sla_total_30d_count > 0 && stats.sla_met_30d_percent !== null;

  const slaSubtext = hasSla
    ? `${formatCount(stats!.sla_met_30d_count)} of ${formatCount(stats!.sla_total_30d_count)} on time`
    : stats
      ? "No responses in window"
      : null;

  const slaDisplay = hasSla ? String(stats!.sla_met_30d_percent) : PLACEHOLDER;

  // Only a below-target SLA is coloured; an on-target figure stays in ink.
  const slaTone: StatTone | undefined = hasSla
    ? stats!.sla_met_30d_percent! >= 95
      ? undefined
      : stats!.sla_met_30d_percent! >= 85
        ? "warning"
        : "danger"
    : undefined;

  const isStaff = me?.role === "iquest_admin" || me?.role === "service_desk";
  const myOpen = stats?.my_open_tickets;

  const recentItems = recentTickets?.items ?? [];
  const topApprovals = (pendingApprovals ?? []).slice(0, 4);

  // A non-zero count is flagged with a badge instead of a plain foot line.
  const pendingFlag =
    !!stats &&
    stats.approval_engine_status !== "not_yet_built" &&
    stats.my_pending_requests > 0;
  const pendingSub =
    stats?.approval_engine_status === "not_yet_built"
      ? "Coming soon"
      : stats && stats.my_pending_requests === 0
        ? "Nothing waiting on you"
        : null;

  return (
    <div className="rf-wrap">
      <div className="rf-pagehead">
        <h1 className="rf-h1" ref={headingRef} tabIndex={-1}>
          Dashboard
        </h1>
        <p className="rf-sub">
          {me || !online || meError
            ? `${greeting(me?.display_name)} ${
                isAllHealthy(stats) ? "You're all caught up." : "Here's your support at a glance."
              }`
            : "Loading…"}
        </p>
      </div>

      {/* The live region is always mounted and holds text only. Try again sits beside it. */}
      <div
        className={!online || onlineFailure ? "form-error" : undefined}
        style={!online || onlineFailure ? { marginBottom: 16 } : undefined}
      >
        <p role="status" style={{ margin: online && onlineFailure ? "0 0 8px" : 0 }}>
          {!online ? OFFLINE_NOTICE : onlineFailure ? FAILURE_NOTICE : ""}
        </p>
        {online && onlineFailure && (
          <button
            ref={retryRef}
            type="button"
            className="btn btn-ghost"
            onClick={retryFailed}
            aria-disabled={retrying || undefined}
          >
            Try again
          </button>
        )}
      </div>

      <div className="rf-stats">
        <StatCard
          to="/tickets?scope=my&status=open"
          label="My open tickets"
          value={stats ? formatCount(stats.my_open_tickets) : PLACEHOLDER}
          sub={
            statsUnavailable
              ? STATS_UNAVAILABLE
              : myOpen === undefined || myOpen === null
                ? undefined
                : myOpen > 0
                  ? "Open in your name"
                  : "Nothing open in your name"
          }
          action="View my tickets"
        />
        <StatCard
          to={noCompanyAccess ? undefined : "/tickets?scope=company&status=open"}
          label="Company open tickets"
          value={
            noCompanyAccess || !stats
              ? PLACEHOLDER
              : formatCount(stats.company_open_tickets)
          }
          sub={
            statsUnavailable
              ? STATS_UNAVAILABLE
              : noCompanyAccess
                ? "No access"
                : isStaff
                  ? "Across all clients"
                  : me?.company_name
                    ? `Across ${me.company_name}`
                    : undefined
          }
          subTitle={
            noCompanyAccess
              ? "Company-wide ticket counts require the view_all_company_tickets permission."
              : undefined
          }
          action={noCompanyAccess ? "Ask your admin for access" : "View all tickets"}
        />
        <StatCard
          to="/services/requests"
          label="My pending requests"
          value={stats ? formatCount(stats.my_pending_requests) : PLACEHOLDER}
          sub={statsUnavailable ? STATS_UNAVAILABLE : pendingSub ?? undefined}
          flag={pendingFlag ? "Needs your approval" : undefined}
          action="Review requests"
        />
        <StatCard
          to={features.sla ? "/sla" : undefined}
          tone={slaTone}
          label="Response SLA · 30d"
          labelTitle="Share of tickets answered within the first-response SLA over the last 30 days."
          value={stats ? slaDisplay : PLACEHOLDER}
          unit={hasSla ? "%" : undefined}
          sub={statsUnavailable ? STATS_UNAVAILABLE : slaSubtext ?? undefined}
          action={features.sla ? "Open SLA report" : "Measured on first response"}
        />
      </div>

      <div className="rf-dash">
        <div className="rf-datacard">
          <div className="rf-datacard-head">
            <span className="t">Recent tickets</span>
            <Link className="lnk" to="/tickets">
              View all <Icon name="arrowRight" size={15} />
            </Link>
          </div>
          {recentUnavailable ? (
            <div className="rf-datacard-empty">{RECENT_UNAVAILABLE}</div>
          ) : recentTickets === undefined ? (
            <div className="rf-datacard-empty">Loading…</div>
          ) : recentItems.length === 0 ? (
            <div className="rf-datacard-empty">No tickets in the window.</div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>SLA</th>
                    <th>Age</th>
                  </tr>
                </thead>
                <tbody>
                  {recentItems.map((t, i) => {
                    const pill = slaPill(t.sla_posture);
                    return (
                      <tr key={t.ticketNumber ?? t.id ?? `row-${i}`}>
                        <td>
                          {t.ticketNumber ? (
                            <Link className="rf-tlink" to={`/tickets/${t.ticketNumber}`}>
                              {t.title ?? "(untitled)"}
                            </Link>
                          ) : (
                            <span className="rf-tlink">{t.title ?? "(untitled)"}</span>
                          )}
                          {t.ticketNumber && <div className="rf-tnum">{t.ticketNumber}</div>}
                        </td>
                        <td>
                          <span className={statusBadgeClass(t.status_label)}>
                            {t.status_label ?? "—"}
                          </span>
                        </td>
                        <td>
                          <span className="rf-prio">
                            <span className={`priority-dot ${priorityClass(t.priority_label)}`} />
                            {t.priority_label ?? "—"}
                          </span>
                        </td>
                        <td>
                          {pill && <span className={pill.cls}>{pill.label}</span>}
                        </td>
                        <td>{fmtAge(t.age_hours)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div>
          {topApprovals.length > 0 && (
            <div className="rf-railcard">
              <div className="rf-railcard-head">
                <span className="rf-railcard-title">Needs your approval</span>
                <Link className="lnk" to="/services/approvals">
                  All
                </Link>
              </div>
              {topApprovals.map((a) => (
                <Link
                  key={a.assignment_id}
                  className="rf-approval"
                  to="/services/approvals"
                >
                  <span className="av">{initials(a.requestor_display_name)}</span>
                  <span className="bd">
                    <span className="t">{humanizeSlug(a.form_type_slug)}</span>
                    <span className="s">
                      {a.requestor_display_name ? `${a.requestor_display_name} · ` : ""}
                      submitted {relativeTime(a.submitted_at)}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          )}

          <div className="rf-railcard">
            <div className="rf-railcard-head">
              <span className="rf-railcard-title">Quick actions</span>
            </div>
            <div className="rf-quick">
              <RaiseTicketLink
                className="rf-quickbtn"
                impersonating={!!me?.impersonating}
                returnPath="/"
                focusKey={DASHBOARD_RAISE_KEY}
              >
                <Icon name="ticket" size={17} />
                Raise a ticket
                <span className="arr">
                  <Icon name="arrowRight" size={15} />
                </span>
              </RaiseTicketLink>
              <Link className="rf-quickbtn" to="/services">
                <Icon name="doc" size={17} />
                Browse online forms
                <span className="arr">
                  <Icon name="arrowRight" size={15} />
                </span>
              </Link>
              {features.kb && (
                <Link className="rf-quickbtn" to="/kb">
                  <Icon name="book" size={17} />
                  Search knowledge base
                  <span className="arr">
                    <Icon name="arrowRight" size={15} />
                  </span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

type StatTone = "warning" | "danger";

/**
 * A ticket-stub stat card: the figure, a perforated tear line, then an action
 * stub. Renders as a deep-linking anchor when `to` is set, or a static card
 * (no hover lift, plain-text stub) when omitted, e.g. no access.
 */
function StatCard({
  to,
  tone,
  label,
  labelTitle,
  value,
  unit,
  sub,
  subTitle,
  flag,
  action,
}: {
  to?: string;
  tone?: StatTone;
  label: string;
  labelTitle?: string;
  value: string;
  unit?: string;
  sub?: string;
  subTitle?: string;
  /** Replaces the foot line with an amber badge, for a count that needs the user. */
  flag?: string;
  action: string;
}) {
  // A zero or no-data figure is muted so the cards that need attention stand
  // out. A tone wins, so a 0% SLA still reads as a breach.
  const muted = value === "0" || value === PLACEHOLDER;
  const valueClass = `rf-stub-value${tone ? ` tone-${tone}` : muted ? " is-muted" : ""}`;
  const body = (
    <>
      <div className="rf-stub-body">
        <div className="rf-stub-label" title={labelTitle}>
          {label}
        </div>
        <div className={valueClass}>
          {value}
          {unit && <span className="rf-stub-unit">{unit}</span>}
        </div>
        <div className="rf-stub-foot" title={subTitle}>
          {flag ? <span className="badge badge-warning">{flag}</span> : sub}
        </div>
      </div>
      <div className="rf-stub-tear" aria-hidden="true" />
      <div className={`rf-stub-action${to ? "" : " is-static"}`}>
        {action}
        {to && <Icon name="arrowRight" size={16} />}
      </div>
    </>
  );
  if (to) {
    return (
      <Link className="rf-stub" to={to}>
        {body}
      </Link>
    );
  }
  return <div className="rf-stub">{body}</div>;
}
